import {
  PrimaryButton,
  TextField,
  IconButton,
  Stack,
} from '@fluentui/react';
import useAxios from 'axios-hooks';
import React, { useState } from 'react';
import Select from 'react-select';
import { FixedSizeList as List } from 'react-window';
import { FixedSizeList as VirtualizedList } from 'react-window';
import { Checkbox } from '@fluentui/react';
import Toast from '../../../../Forms/Toast';
import FeatureSummary from '../../../../Util/FeatureSummary';

const deleteIcon = { iconName: 'Delete' };

const CustomMenuList = ({ children, ...props }) => {
  const height = 35;
  const itemCount = children.length;
  const totalHeight = Math.min(300, itemCount * height);

  return (
    <List height={totalHeight} itemCount={itemCount} itemSize={height} width="100%">
      {({ index, style }) => (
        <div
          style={{
            ...style,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            padding: '0 8px',
            boxSizing: 'border-box',
          }}
          title={children[index]?.props?.children}
        >
          {children[index]}
        </div>
      )}
    </List>
  );
};

const EligibleUsers = () => {
  const [cobrandId, setCobrandId] = useState();
  const [username, setUsername] = useState('');
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showToast, setShowToast] = React.useState(false);
  const [checkOnSignup, setCheckOnSignup] = useState(false);
  const [checkOnLogin, setCheckOnLogin] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [{ data: cobrands }] = useAxios('/internal/cobrands');
  const cobrandsOptions = cobrands?.map(u => ({ value: u.cobrandId, label: u.name }));

  const [{ data: users }, fetchEligibleUsers] = useAxios(
    { url: `internal/cobrand/0/eligible-users`, method: 'GET' },
    { manual: true }
  );

  const [{ data: cobrandSettings }, fetchEligibleUserCobrandSettings] = useAxios(
    { url: `internal/cobrand/0/eligible-user-cobrand-settings`, method: 'GET' },
    { manual: true }
  );

  const handleCobrandChange = async (option) => {
    const selectedCobrandId = option.value;
    setCobrandId(selectedCobrandId);

    const result = await fetchEligibleUsers({
      url: `internal/cobrand/${selectedCobrandId}/eligible-users`,
      method: 'GET',
    });
    const result2 = await fetchEligibleUserCobrandSettings({
      url: `internal/cobrand/${selectedCobrandId}/eligible-user-cobrand-settings`,
      method: 'GET',
    });

    if (!result.error) {
      setEligibleUsers(result.data);
    }
    if (!result2.error) {
      setCheckOnSignup(result2.data.checkEligibleUsersOnSignup);
      setCheckOnLogin(result2.data.checkEligibleUsersOnLogin);
    }
  };

  const [{ data, loading, error }, executeDelete] = useAxios(
    { url: 'internal/cobrand/eligible-users/remove-user', method: 'POST', }, { manual: true });

  const handleDeleteUser = async (cobrandEligibleUserId) => {
    var model = {
      CobrandEligibleUserId: cobrandEligibleUserId
    }
    const result = await executeDelete({ data: model });
    if (!result.isAxiosError) {
      setShowToast(true);
    }

    setEligibleUsers(prev =>
      prev.filter(user => user.cobrandEligibleUserId !== cobrandEligibleUserId)
    );
  };

  const filteredUsers = eligibleUsers.filter(user =>
    user.emailAddress?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [{ }, executeAdd] = useAxios(
    { url: 'internal/cobrand/eligible-users/add-user', method: 'POST', }, { manual: true });

  const addUser = async () => {
    var model = {
      CobrandId: cobrandId,
      EmailAddress: username
    }
    const result = await executeAdd({ data: model });
    if (!result.isAxiosError) {
      setShowToast(true);
      setUsername('');
      const result2 = await fetchEligibleUsers({
        url: `internal/cobrand/${cobrandId}/eligible-users`,
        method: 'GET',
      });
      if (!result2.error) {
        setEligibleUsers(result2.data);
      }
    }
  }

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const [{ }, executePostSettings] = useAxios(
    { url: 'internal/cobrand/eligible-users/cobrand-settings-set', method: 'POST' },
    { manual: true }
  );

  const handleCheckboxChange = (setter) => (ev, checked) => {
    setter(checked);
    setHasChanged(true);
  };

  const handleSaveSettings = async () => {
    const result = await executePostSettings({
      data: {
        CobrandId: cobrandId,
        CheckEligibleUsersOnSignup: checkOnSignup,
        CheckEligibleUsersOnLogin: checkOnLogin,
      },
    });

    if (!result.error) {
      setShowToast(true);
      setHasChanged(false);
    }
  };

  return (
    <>
      <h4>Select Cobrand</h4>
      <div className="mt-1 mb20">
        <label className="default-label mt20">Select Cobrand</label>
        <Select
          components={{ MenuList: CustomMenuList }}
          options={cobrandsOptions}
          placeholder="Select Cobrand"
          name="cobrandId"
          value={cobrandsOptions?.find(option => option.value === cobrandId)}
          onChange={handleCobrandChange}
          isSearchable
          styles={{
            menuList: base => ({ ...base, maxHeight: '300px', overflowY: 'auto' }),
            container: base => ({ ...base, width: 400 }),
            control: base => ({ ...base, border: '1px solid #aaaaaa' }),
          }}
        />
      </div>

      {cobrandId && <Stack
        styles={{
          root: {
            width: 520,
            padding: 16,
            marginTop: 20,
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            borderRadius: 4,
            background: '#fff',
          },
        }}
        tokens={{ childrenGap: 12 }}
      >
        <Checkbox
          label="Check Eligible Users On Signup"
          checked={checkOnSignup}
          onChange={handleCheckboxChange(setCheckOnSignup)}
        />
        <Checkbox
          label="Check Eligible Users On Login"
          checked={checkOnLogin}
          onChange={handleCheckboxChange(setCheckOnLogin)}
        />
        <PrimaryButton
          text="Save Settings"
          onClick={handleSaveSettings}
          disabled={!hasChanged || !cobrandId}
        />
      </Stack>}

      {(checkOnLogin || checkOnSignup) && <Stack
        styles={{
          root: {
            width: 520,
            padding: 16,
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            borderRadius: 4,
            background: '#fff',
            marginTop: 20,
          },
        }}
      >
        <div className="mt-1 mb20 displayflex aligncenter">
          <TextField
            placeholder="User Name"
            name="domainName"
            value={username}
            onChange={(e, newValue) => setUsername(newValue)}
            styles={{ root: { width: 290 } }}
          />
          <PrimaryButton
            className="ml10"
            onClick={() => addUser()}
            text="Add User"
            disabled={!username || !isValidEmail(username)}
          />
        </div>
        <TextField
          placeholder="Search users"
          value={searchTerm}
          onChange={(e, newValue) => setSearchTerm(newValue || '')}
          styles={{ root: { marginBottom: 12 } }}
        />

        {filteredUsers?.length === 0 ? (
          <span>No users found.</span>
        ) : (
          <VirtualizedList
            height={400}
            itemCount={filteredUsers?.length}
            itemSize={48}
            width="100%"
          >
            {({ index, style }) => {
              const user = filteredUsers[index];
              return (
                <div
                  style={{
                    ...style,
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: '1px solid #eee',
                    padding: '0 8px',
                    boxSizing: 'border-box',
                  }}
                  key={user.cobrandEligibleUserId}
                >
                  <span style={{ flexGrow: 1 }}>{user.emailAddress}</span>
                  <IconButton
                    iconProps={deleteIcon}
                    title="Remove"
                    ariaLabel="Remove"
                    onClick={() => handleDeleteUser(user.cobrandEligibleUserId)}
                  />
                </div>
              );
            }}
          </VirtualizedList>
        )}
      </Stack>}
      <Toast show={showToast} message="Saved" onClose={() => setShowToast(false)} />
      <FeatureSummary
          label="About Eligible Users"
          summary="Manage the list of eligible users for a cobrand. Add or remove email addresses that are authorized to register or log in. Configure whether eligibility is checked during signup, login, or both."
          imageUrlPath="eligible-users"
      />
    </>
  );
};

export default EligibleUsers;
